import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Divider,
  Typography,
  MenuItem,
  Fab,
  Tooltip,
  ListItemIcon,
  ListItemText,
  IconButton,
} from "@mui/material";

import MuiIcon from "../../../../../../shared/styles/mui/helpers/mui-icon.helper";
import FilterFieldDialogComponent from "./components/filter-field-dialog/filter-field-dialog.component";
import IReportFilterCriteria from "../../../../../../shared/contracts/IReportFilterCriteria";
import useHelpers from "../../../../../../shared/services/helpers/useHelpers.hook";
import MenuButtonComponent from "../../../../../../shared/components/menu-button/menu-button.component";
import useLoader from "../../../../../../shared/components/loading-dialog/useLoader.hook";
import LoadingDialogComponent from "../../../../../../shared/components/loading-dialog/loading-dialog.component";
import useReportingAndAnalyticsApi from "../../../useReportingAndAnalyticsApi";
import ReportDataFiltersDialogService from "./report-data-filters-dialog.service";
import FiltersComponent from "./components/filters/filters.component";
import { FILTER_CATEGORIES } from "../../../../../../shared/contracts/TFilterCategory";
import useAppStoreContext from "../../../../../../shared/context/app-store/app-store.context";


export default function ReportDataFiltersDialogComponent({
  open, onClose, onApply,
}: {
  open: boolean;
  onClose: () => void;
  onApply: () => void;
}) {
  const { hasDifference } = useHelpers();

  const [hasValidEditChanges, setHasValidEditChanges] =
    useState<boolean>(false);
  const [hasValidSaveChanges, setHasValidSaveChanges] =
    useState<boolean>(false);
  const [isPinned, setIsPinned] = useState<boolean>(false);
  const [listFilters, setListFilters] = useState<IReportFilterCriteria[]>([]);
  const [targetFilterItem, setTargetFilterItem] =
    useState<IReportFilterCriteria | null>(null);
  const [targetFilterIndex, setTargetFilterIndex] = useState<number>(-1);

  const {
    selReportDetails,
    defaultAvailableColumns,
    currentFilters,
    setCurrentFilters,
    defaultFilters,
    setDefaultFilters,
  } = useAppStoreContext();

  const { saveFilters } = useReportingAndAnalyticsApi();

  const saveFiltersLoader = useLoader({
    loaderName: "Save Filters",
    loadAsync: () =>
      saveFilters(
        ReportDataFiltersDialogService.getSaveFiltersBody(listFilters),
        {
          doIf: () => hasValidSaveChanges && saveFiltersLoader.isNotLoading,
          doBefore: () => {
            saveFiltersLoader.SET_LOADING("Saving Filters...");
          },
          onFulfilled: (data) => {
            setDefaultFilters([...listFilters]);
            handleApply();
            saveFiltersLoader.SET_SUCCESS("Success.!");
          },
          onException: saveFiltersLoader.SET_EXCEPTION,
        }
      ),
  });

  useEffect(() => setListFilters([...currentFilters]), [currentFilters]);

  useEffect(() => {
    const hasEditChanges =
      listFilters.length > 0 && hasDifference(currentFilters, listFilters);
    setHasValidEditChanges(hasEditChanges);

    const hasSaveChanges =
      currentFilters.length > 0 &&
      hasDifference(currentFilters, defaultFilters);
    setHasValidSaveChanges(hasEditChanges || hasSaveChanges);
  }, [hasDifference, listFilters, currentFilters, defaultFilters]);

  const handleAddNewFilterItem = () => {
    setTargetFilterIndex(-1);
    setTargetFilterItem({
      column: "",
      columnDescription: null,
      operator: "",
      aliasTable: "",
      description: "",
      guid: "",
      value: "",
      operatorValueId: 0
    });
  };

  const handleEditClick = (
    filterIndex: number,
    filterItem: IReportFilterCriteria
  ) => {
    setTargetFilterIndex(filterIndex);
    setTargetFilterItem({ ...filterItem });
  };

  const handleDeleteClick = (
    filterIndex: number,
    filterItem: IReportFilterCriteria
  ) => {
    const _filters = [...listFilters];
    _filters.splice(filterIndex, 1);
    setListFilters(_filters);
  };

  const handleFilterValueChange = (
    filterIndex: number,
    filterValue: string | null
  ) => {
    const _listFilters = [...listFilters];
    const targetFilterItem = {
      ..._listFilters[filterIndex],
      value: filterValue,
    };

    _listFilters[filterIndex] = targetFilterItem;
    setListFilters([..._listFilters]);
  };

  const handleFilterItemChange = (
    filterIndex: number,
    targetFilterItem: IReportFilterCriteria
  ) => {
    if (filterIndex === -1) {
      setListFilters([...listFilters, targetFilterItem]);
    } else {
      const _listFilters = [...listFilters];
      _listFilters.splice(filterIndex, 1, targetFilterItem);
      setListFilters([..._listFilters]);
    }
    // closes the dialog
    handleDialogCancel();
  };

  const handleDialogCancel = () => {
    setTargetFilterIndex(-1);
    setTargetFilterItem(null);
  };

  const handleApply = () => {
    setCurrentFilters([...listFilters]);
    onApply();
  };

  const handleResetFilters = () => {
    setListFilters([...currentFilters]);
  };

  return (
    <>
      {/* Filters Dialog */}
      <Dialog
        scroll="paper"
        maxWidth="lg"
        fullWidth
        fullScreen={isPinned}
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: { borderRadius: "12px", maxHeight: isPinned ? null : "640px" },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Grid container direction="row" wrap="nowrap">
            <Grid
              item
              alignItems="start"
              container
              direction="row"
              wrap="nowrap"
            >
              <Grid item container direction="column" wrap="nowrap">
                <Grid
                  item
                  container
                  direction="row"
                  justifyContent="start"
                  alignItems="center"
                >
                  <Typography
                    color="text.secondary"
                    variant="button"
                    sx={{ textOverflow: "ellipsis" }}
                  >
                    Select Filters
                  </Typography>
                </Grid>

                <Typography flexGrow={1} variant="subtitle1">
                  {selReportDetails?.reportName}
                </Typography>
              </Grid>
            </Grid>

            <Grid
              item
              container
              direction="row"
              alignContent="start"
              justifyContent="center"
            >
              <Tooltip
                title={`${isPinned ? "Collapse" : "Expand"} Dialog`}
                placement="bottom"
              >
                <IconButton
                  onClick={() => setIsPinned(!isPinned)}
                  color="primary"
                  size="small"
                >
                  {isPinned
                    ? MuiIcon.FOLD({ sx: {} })
                    : MuiIcon.UN_FOLD({ sx: {} })}
                </IconButton>
              </Tooltip>
            </Grid>

            <Grid
              item
              container
              direction="row"
              gap={1}
              justifyContent="end"
              wrap="nowrap"
              alignItems="center"
            >
              {/* <Button color='error' variant='text' onClick={onClose} sx={{ mr: 2 }} startIcon={MuiIcon.CANCEL()}>Cancel</Button> */}
              <MenuButtonComponent
                color="error"
                menuButtonColor="secondary"
                text="Cancel"
                onClick={onClose}
                buttonIcon={MuiIcon.CANCEL()}
                size="small"
                variant="text"
                buttonVariant={hasValidEditChanges ? "outlined" : "contained"}
                menuButtonVariant={
                  hasValidEditChanges ? "contained" : "outlined"
                }
              >
                <MenuItem onClick={handleResetFilters}>
                  {" "}
                  {/* TODO: add reload code */}
                  <ListItemIcon>
                    {MuiIcon.RELOAD({
                      sx: { color: (t) => t.palette.error.main },
                    })}
                  </ListItemIcon>
                  <ListItemText sx={{}}>Reset Filters</ListItemText>
                </MenuItem>
              </MenuButtonComponent>

              <MenuButtonComponent
                color="primary"
                menuButtonColor="success"
                text="Apply"
                onClick={handleApply}
                buttonIcon={MuiIcon.APPLY()}
                size="small"
                variant="text"
                buttonVariant="contained"
                menuButtonVariant={
                  hasValidSaveChanges && !hasValidEditChanges
                    ? "contained"
                    : "outlined"
                }
                buttonDisabled={!hasValidEditChanges}
                menuButtonDisabled={!hasValidSaveChanges}
              >
                <MenuItem onClick={() => saveFiltersLoader.loadAsync()}>
                  <ListItemIcon color="success">
                    {MuiIcon.SAVE({
                      sx: { color: (t) => t.palette.success.dark },
                    })}
                  </ListItemIcon>
                  <ListItemText sx={{ color: (t) => t.palette.success.dark }}>
                    Save Filters
                  </ListItemText>
                </MenuItem>
              </MenuButtonComponent>
            </Grid>
          </Grid>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ py: 4 }}>
          <FiltersComponent
            handleAddNewFilterItem={handleAddNewFilterItem}
            handleEditClick={handleEditClick}
            handleDeleteClick={handleDeleteClick}
            listFilters={listFilters}
            onFilterValueChange={handleFilterValueChange}
          />
        </DialogContent>
      </Dialog>

      {/* Add/Edit Dialog */}
      {targetFilterItem && (
        <FilterFieldDialogComponent
          targetFilterIndex={targetFilterIndex}
          targetFilterItem={targetFilterItem}
          defaultAvailableColumns={defaultAvailableColumns}
          filterCategories={FILTER_CATEGORIES}
          onApply={handleFilterItemChange}
          onCancel={handleDialogCancel}
        />
      )}

      {/* Loader Dialog */}
      {<LoadingDialogComponent loaderHook={saveFiltersLoader} />}
    </>
  );
}